/**
 * Standardized Service Layer Error Handling
 * 
 * All service methods should throw ServiceError instances for consistent error handling
 */

export type ErrorCode = 
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'VALIDATION_ERROR'
  | 'DATABASE_ERROR'
  | 'NETWORK_ERROR'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'
  | 'CONFLICT'
  | 'USER_VOICE_FETCH_ERROR'
  | 'USER_VOICE_CREATE_ERROR'
  | 'USER_VOICE_UPDATE_ERROR'
  | 'USER_VOICE_UPSERT_ERROR'
  | 'ICP_FETCH_ERROR'
  | 'ICP_CREATE_ERROR'
  | 'ICP_UPDATE_ERROR'
  | 'ICP_UPSERT_ERROR';

export interface ServiceErrorDetails {
  table?: string;
  operation?: string;
  field?: string;
  originalError?: unknown;
  context?: Record<string, unknown>;
}

export class ServiceError extends Error {
  constructor(
    message: string,
    public code: ErrorCode,
    public statusCode: number = 500,
    public details?: ServiceErrorDetails
  ) {
    super(message);
    this.name = 'ServiceError';
  }

  /**
   * Convert to plain object for logging/API responses
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
    };
  }

  /**
   * Check if error is a ServiceError
   */
  static isServiceError(error: unknown): error is ServiceError {
    return error instanceof ServiceError;
  }

  /**
   * Create error from Supabase error
   */
  static fromSupabaseError(error: unknown, operation: string, table?: string): ServiceError {
    const errorWithMessage = error as { message?: string; code?: string };
    const message = errorWithMessage?.message || 'Database operation failed';
    const code = errorWithMessage?.code;

    // Map Supabase error codes to our error codes
    if (code === '42501' || message.includes('permission denied')) {
      return new ServiceError(
        'You do not have permission to perform this operation',
        'FORBIDDEN',
        403,
        { table, operation, originalError: error }
      );
    }

    if (code === '23505') {
      return new ServiceError(
        'A record with these values already exists',
        'CONFLICT',
        409,
        { table, operation, originalError: error }
      );
    }

    if (code === '23503') {
      return new ServiceError(
        'Related record not found',
        'NOT_FOUND',
        404,
        { table, operation, originalError: error }
      );
    }

    return new ServiceError(
      message,
      'DATABASE_ERROR',
      500,
      { table, operation, originalError: error }
    );
  }

  /**
   * Factory methods for common errors
   */
  static notFound(resource: string, id?: string): ServiceError {
    return new ServiceError(
      id ? `${resource} with id '${id}' not found` : `${resource} not found`,
      'NOT_FOUND',
      404,
      { context: { resource, id } }
    );
  }

  static unauthorized(message: string = 'Authentication required'): ServiceError {
    return new ServiceError(message, 'UNAUTHORIZED', 401);
  }

  static forbidden(message: string = 'You do not have permission to perform this action'): ServiceError {
    return new ServiceError(message, 'FORBIDDEN', 403);
  }

  static validation(message: string, field?: string): ServiceError {
    return new ServiceError(message, 'VALIDATION_ERROR', 400, { field });
  }

  static conflict(message: string, details?: ServiceErrorDetails): ServiceError {
    return new ServiceError(message, 'CONFLICT', 409, details);
  }

  static rateLimited(message: string = 'Rate limit exceeded. Please try again later.'): ServiceError {
    return new ServiceError(message, 'RATE_LIMITED', 429);
  }

  static internal(message: string, context?: Record<string, unknown>): ServiceError {
    return new ServiceError(message, 'INTERNAL_ERROR', 500, { context });
  }
}

/**
 * Wrap async service operations with error handling
 */
export async function withServiceErrorHandling<T>(
  operation: () => Promise<T>,
  context: { operation: string; table?: string }
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (ServiceError.isServiceError(error)) {
      throw error;
    }
    throw ServiceError.fromSupabaseError(error, context.operation, context.table);
  }
}
